import { z } from 'zod';
import { IsoDateTime, UuidString } from '../primitives.js';

/**
 * ItemMatchingDecision — audit-запись решения LLM-matcher-а при импорте номенклатуры
 * из внешних источников (МЭ 1С inventory, цех Excel). Хранится навсегда для
 * воспроизводимости: при повторном импорте можем увидеть, что matcher решил раньше.
 *
 * Инвариант: каждая auto-match запись сопровождается AuditEvent
 * (action='item_dedup_automatch', actor='llm:<model>').
 */
export const MatchVerdict = z.enum(['auto_match', 'needs_review', 'no_match']);
export type MatchVerdict = z.infer<typeof MatchVerdict>;

export const MatchEvidence = z.object({
  name_overlap: z.string(),
  uom_compatible: z.boolean(),
  price_delta_pct: z.number().nullable(),
  spec_signal: z.string().nullable(),
});
export type MatchEvidence = z.infer<typeof MatchEvidence>;

export const MatchAlternativeCandidate = z.object({
  workshop_id: z.string(),
  score: z.number().min(0).max(1),
  reason: z.string(),
});
export type MatchAlternativeCandidate = z.infer<typeof MatchAlternativeCandidate>;

export const ItemMatchingDecision = z.object({
  id: UuidString,
  entity_id: UuidString,
  /** Идентификатор источника «A» — обычно 1С номенклатурный код. */
  source_a_key: z.string().min(1),
  /** Идентификатор источника «B» — цеховой код/позиция в Excel. */
  source_b_key: z.string().nullable(),
  /** Созданный или обновлённый item_id (null если verdict=no_match). */
  item_id: UuidString.nullable(),
  verdict: MatchVerdict,
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  evidence: MatchEvidence.nullable().default(null),
  alternatives: z.array(MatchAlternativeCandidate).default([]),
  /** Использованная LLM-модель (например, 'claude-haiku-5-5'); allback* — решение без модели. */
  llm_model: z.string().nullable().default(null),
  /** Использование токенов. */
  llm_usage: z
    .object({ input_tokens: z.number().int(), output_tokens: z.number().int() })
    .nullable()
    .default(null),
  /** Результат после человеческого ревью (если был). */
  human_reviewed_by: UuidString.nullable().default(null),
  human_reviewed_at: IsoDateTime.nullable().default(null),
  human_verdict: MatchVerdict.nullable().default(null),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type ItemMatchingDecision = z.infer<typeof ItemMatchingDecision>;
